import { t, useUiLocale } from '@/i18n/locale';
import '@/components/navigation/navigationStrip.css';
import { useNavigationVisibility } from '@/components/navigation/useNavigationVisibility';
import { Link, useLocation } from 'react-router-dom';
import { useMe } from '@/auth/useAuth';
import { filterStudyNavigation, useStudyAvailability } from '@/pages/learn/study/studyAvailability';
import { LEARN_DOCK_ITEMS, WALK_IN_DOCK_ITEMS, learnSectionPath } from './learnNavigation';

export function LearnDock({ inFlow = false }: { inFlow?: boolean }) {
  useUiLocale();
  const me = useMe('kid');
  const { pathname } = useLocation();
  const walkIn = me.data?.kind === 'kid' && me.data.is_ephemeral === true;
  const availability = useStudyAvailability(me.data?.kind === 'kid' && !walkIn);
  const items = walkIn
    ? WALK_IN_DOCK_ITEMS
    : filterStudyNavigation(LEARN_DOCK_ITEMS, availability.data);
  const selected = walkIn
    ? items.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))?.to
    : learnSectionPath(pathname);

  const { navigation, revealFocusedLink } = useNavigationVisibility<HTMLDivElement>(
    `${pathname}:${items.map(({ to }) => to).join('|')}`,
  );

  return (
    <nav
      aria-label={t('Kids navigation')}
      data-testid="learn-navigation-dock"
      className={`${inFlow ? 'relative my-2 mb-[calc(0.5rem+env(safe-area-inset-bottom))] w-[calc(100%_-_1.5rem)] shrink-0' : 'fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))]'} z-30 mx-auto max-w-[46rem] rounded-full border border-ink/15 bg-canvas-pure p-1.5 shadow-floating-chrome`}
    >
      <div
        ref={navigation}
        onFocusCapture={revealFocusedLink}
        className="navigation-strip flex min-h-14 gap-1 overflow-x-auto overscroll-x-contain p-1"
      >
        {items.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            aria-current={selected === to ? 'page' : undefined}
            className={`flex min-h-14 min-w-11 grow shrink-0 flex-col items-center justify-center gap-1 whitespace-nowrap rounded-full px-1 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral motion-reduce:transition-none ${selected === to ? 'bg-wash-coral text-ink' : 'text-slate2 hover:bg-surface'}`}
          >
            <Icon aria-hidden="true" size={22} strokeWidth={1.5} />
            <span>{t(label)}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
}
